import { widthInCm, heightInBrick, even, odd, getCurrentGroup } from 'helpers/utils';

const firstBrick = (brick) => brick === 1;

const lastBrick = (brick, element) => brick === element.brickCountHorizontally;

const higherThan = (element, comparedElement) => {
  if (!element || !comparedElement) return 0;
  return (
    element.brickCountVertically +
    element.elevation -
    (comparedElement.brickCountVertically + comparedElement.elevation)
  );
};

const isConnectToOtherElement = (row, element, otherElement) => {
  return otherElement && row > higherThan(element, otherElement);
};

const getPrevConnectBricks = (prevElement) => {
  if (prevElement) {
    return prevElement.bricks.filter((brick) => brick.type === 'connectNext');
  }

  return [];
};

const getPrevFirstConnectBrick = (prevConnectBricks) => {
  if (prevConnectBricks.length) {
    return prevConnectBricks.reduce(
      (maxBrick, currentBrick) => currentBrick && (currentBrick.x > maxBrick.x ? currentBrick : maxBrick)
    );
  }

  return 0;
};

const checkStartWithHalf = (element, prevElement, prevConnectBricks, prevFirstConnectBrick) => {
  if (!prevElement || prevElement.name === 'door') {
    return odd(element.brickCountVertically + element.elevation);
  }

  if (!prevConnectBricks.length) {
    return false;
  }

  if (higherThan(prevElement, element) < 0) {
    if (prevFirstConnectBrick.y === 2 && higherThan(element, prevElement) % 2 === 1) {
      return true;
    }

    if (prevFirstConnectBrick.y === 1 && higherThan(element, prevElement) % 2 === 0) {
      return true;
    }

    return false;
  } else {
    return prevFirstConnectBrick.y - higherThan(prevElement, element) === 1 ? true : false;
  }
};

const generateBrickTypes = (element, prevElement, nextElement) => {
  const bricks = [];
  const prevConnectBricks = getPrevConnectBricks(prevElement);
  const prevFirstConnectBrick = getPrevFirstConnectBrick(prevConnectBricks);
  const startWithHalf = checkStartWithHalf(element, prevElement, prevConnectBricks, prevFirstConnectBrick);

  let x;
  let y;
  let normalBrick = true;
  let connectWithPrev = false;
  let connectWithNext = false;
  let brickType;

  for (y = 1; y <= element.brickCountVertically; y++) {
    for (x = 1; x <= element.brickCountHorizontally; x++) {
      if (startWithHalf) {
        if (odd(y) && (firstBrick(x) || lastBrick(x, element))) {
          normalBrick = false;
        }

        if (odd(x) && lastBrick(x, element)) {
          normalBrick = false;
        }
      } else {
        if (even(y) && (firstBrick(x) || lastBrick(x, element))) {
          normalBrick = false;
        }

        if (odd(y) && lastBrick(x, element)) {
          normalBrick = false;
        }
      }

      if (isConnectToOtherElement(y, element, prevElement) && !normalBrick && firstBrick(x)) {
        connectWithPrev = true;
      }

      /*const oddPrevConnectBrick = odd(prevFirstConnectBrick.y);
      const oddHigherThanPrev = odd(higherThan(element, prevElement));
      const oddY = odd(y);

      if (
        (oddPrevConnectBrick && ((oddHigherThanPrev && oddY) || (!oddHigherThanPrev && !oddY))) ||
        (!oddPrevConnectBrick && ((oddHigherThanPrev && !oddY) || (!oddHigherThanPrev && oddY)))
      ) {
        connectWithPrev = false;
      }*/

      if (isConnectToOtherElement(y, element, nextElement) && !normalBrick && lastBrick(x, element)) {
        connectWithNext = true;
      }

      if (
        element.brickCountHorizontally === 1 &&
        ((even(element.brickCountVertically) && even(y)) || (odd(element.brickCountVertically) && odd(y)))
      ) {
        connectWithNext = false;
      }

      brickType = normalBrick ? 'normal' : 'half';

      if (connectWithPrev) {
        brickType = 'connectPrev';
      }

      if (connectWithNext) {
        brickType = 'connectNext';
      }

      bricks.push({ x, y, type: brickType });

      if (normalBrick) {
        x++;
      }

      connectWithPrev = false;
      connectWithNext = false;
      normalBrick = true;
    }
  }

  return bricks;
};

const addVerticalElementProps = (elements, slopeDirection, fenceMaxWidth, totalDoorsWidth, maxSlope) => {
  const ascSlope = slopeDirection === 'asc';
  const totalSlopeWidth = fenceMaxWidth - totalDoorsWidth;
  let slopeWidthBefore = 0;
  let slopeWidthAfter = 0;
  let slopeTopPercentage;
  let slopeBottomPercentage;
  let elevationInBricks = 0;
  let startY;
  let endY;

  elements.forEach((element) => {
    if (element.name !== 'door') {
      slopeWidthBefore += element.width;
      slopeWidthAfter = totalSlopeWidth - slopeWidthBefore + element.width;
    } else {
      slopeWidthAfter = totalSlopeWidth - slopeWidthBefore;
    }
    slopeTopPercentage = 100 / (totalSlopeWidth / slopeWidthBefore);
    slopeBottomPercentage = 100 / (totalSlopeWidth / slopeWidthAfter);

    if (ascSlope) {
      startY = (slopeBottomPercentage / 100) * maxSlope;
      endY = element.name === 'door' ? startY : maxSlope - (slopeTopPercentage / 100) * maxSlope;
      elevationInBricks = heightInBrick(maxSlope - startY);
    } else {
      startY = maxSlope - (slopeBottomPercentage / 100) * maxSlope;
      endY = element.name === 'door' ? startY : (slopeTopPercentage / 100) * maxSlope;
      elevationInBricks = heightInBrick(maxSlope - endY);
    }

    element.start.y = startY;
    element.end.y = endY;
    element.elevation = Math.floor(elevationInBricks);
  });
};

const getShortestAdjacentPosition = (prevElement, nextElement) => {
  let prevElementHeight = 0;
  let nextElementHeight = 0;

  if (
    (!prevElement || (prevElement && prevElement.name === 'door')) &&
    (!nextElement || (nextElement && nextElement.name === 'door'))
  ) {
    return 0;
  }

  if (prevElement && prevElement.name !== 'door' && (!nextElement || (nextElement && nextElement.name === 'door'))) {
    return -1;
  }

  if (nextElement && nextElement.name !== 'door' && (!prevElement || (prevElement && prevElement.name === 'door'))) {
    return 1;
  }

  if (prevElement && prevElement.name !== 'door') {
    prevElementHeight = prevElement.brickCountVertically;
  }

  if (nextElement && nextElement.name !== 'door') {
    nextElementHeight = nextElement.brickCountVertically;
  }

  if (prevElementHeight + prevElement.elevation <= nextElementHeight + nextElement.elevation) {
    return -1;
  }

  return 1;
};

export const getTotalFenceWidth = (elements) => {
  return Math.ceil(
    elements.reduce((acc, element) => {
      return element.cmHorizontally ? acc + element.cmHorizontally : acc + widthInCm(element.brickCountHorizontally);
    }, 0)
  );
};

export const setElementProps = (fenceGroup, totalFenceWidth) => {
  const elements = [];

  let totalDoorsWidth = 0;
  let cmBeforeElement = 0;
  let cmWidthElemenet = 0;
  let startElement;
  let endElement;

  fenceGroup.elements.forEach((element, index) => {
    cmWidthElemenet = 0;

    if (element.name === 'door') {
      cmWidthElemenet = element.cmHorizontally;
      totalDoorsWidth += element.cmHorizontally;
    } else {
      cmWidthElemenet = widthInCm(element.brickCountHorizontally);
    }

    if (index === 0) {
      startElement = { x: 0 };
      endElement = { x: cmWidthElemenet };
    } else if (fenceGroup.elements.length - 1 === index) {
      startElement = { x: cmBeforeElement };
      endElement = { x: totalFenceWidth };
    } else {
      startElement = { x: cmBeforeElement };
      endElement = { x: cmBeforeElement + cmWidthElemenet };
    }

    elements.push({
      width: cmWidthElemenet,
      start: startElement,
      end: endElement,
      ...element,
    });

    cmBeforeElement += cmWidthElemenet;
  });

  addVerticalElementProps(
    elements,
    fenceGroup.settings.slopeDirection,
    fenceGroup.settings.maxWidth,
    totalDoorsWidth,
    fenceGroup.settings.slopeSizeCm
  );

  elements.forEach((element, index) => {
    const prevElement = elements[index - 1];
    const nextElement = elements[index + 1];

    element.bricks = generateBrickTypes(element, prevElement, nextElement);
    element.shortestAdjacentIndex = getShortestAdjacentPosition(prevElement, nextElement);
  });

  return {
    elements,
    totalDoorsWidth,
  };
};
